import fetch from 'isomorphic-fetch'
import { fromPairs, get, isNil, noop, omitBy, size } from 'lodash'
import { getSessionToken } from '../src/actions/Auth'
import sessionxtd from 'core/sessionxtd'
import { simpleHash } from 'core/helpers'

const logOutCodes = [
  'loginRequired',
  'sessionNotFound',
]

const httpErrorsNotToLog = [
  401,
  403,
  404,
]

export default {

  get(url, params, opts) {
    return buildRequest('GET', url, params, opts)
  },

  post(url, data, opts) {
    return buildRequest('POST', url, data, opts)
  },

  upload(url, file, onProgress = noop) {
    return new Promise(function(resolve, reject) {
      const request = new XMLHttpRequest()
      request.upload.addEventListener('progress', function(e) {
        onProgress({
          loaded: e.loaded,
          total: e.total,
        })
      })
      request.addEventListener('load', resolve)
      request.addEventListener('error', reject)
      request.open('PUT', url)
      request.send(file)
    })
  },
}

/**
 * Build and execute remote request
 * @param method
 * @param url
 * @param params
 * @param opts
 */
async function buildRequest(method, endpoint, params, opts = {}) {
  const url = `${CONFIG.api}${endpoint}`
  const query = (method === 'GET' && params ? toQueryString(params) : '')
  const headers = {
    'content-type': 'application/json',
    'x-auth-token': getSessionToken().token,
  }
  let cacheKey = ''

  if (opts.safeMode) {
    headers['x-safemode'] = opts.safeMode
  }
  if (opts.xAuthToken) {
    headers['x-auth-token'] = opts.xAuthToken
  }
  if (opts.identifier) {
    headers['x-identifier'] = opts.identifier
  } else if (CONFIG.identifier) {
    headers['x-identifier'] = CONFIG.identifier
  }

  //check if request is in sessionStorage
  if (opts.cache) {
    cacheKey = generateCacheKey(url, params)

    const response = sessionxtd.getItem(cacheKey)
    if (response) {
      return { result: response, cache: true }
    }
  }

  const request = {
    method,
    headers,
    mode: 'cors',
    credentials: 'omit', // omit | include
    cache: 'default',
  }

  if (method === 'POST' && size(params)) {
    request.body = JSON.stringify(params || {})
  }

  const remoteURL = ((
    url.startsWith('/') || url.startsWith('http')
  ) ? url : ('/' + url)) + query

  let responseRaw
  let responseHeaders
  let status

  try {
    responseRaw = await fetch(remoteURL, request)
    responseHeaders = responseRaw ? fromPairs([...responseRaw.headers]) : {}
    status = responseRaw.status

    if (status >= 403) {
      const response = await responseRaw.json()
      if (logOutCodes.includes(response.error.code)) {
        localStorage.removeItem('auth')
      }
      throw (response.error || response)
    }

    const response = await processResponse(responseRaw)
    if (response.result !== undefined) {
      if (opts.cache) {
        //store response in sessionStorage
        sessionxtd.setItem(cacheKey, response.result, opts.expiry || 60 * 60 * 1000)
        return { result: response.result, cache: false }
      }
      return response.result
    } else if (response.error) {
      throw response.error
    } else {
      throw response
    }

  } catch (error) {
    if (!httpErrorsNotToLog.includes(status)) {
      logServiceError(error, remoteURL, status, request.body, responseHeaders)
    }
    throw error
  }
}

/**
 * Decide what to do with the response
 * @param response
 */
function processResponse(response) {
  if (response.headers.get('content-type').includes('/json')) {
    return response.json()
  } else {
    return response.text()
  }
}

/**
 * Generates a key consisting of the request url and unique hash based on the userUid and request parameters
 * @param {string} url : request url
 * @param {object} params : request params
 * @returns {string} : Example: 'ccp/userData/getUserPictures:-614443294'
 */
export function generateCacheKey(url, params) {
  return url.split('/').splice(-3).join('/')
    + ':'
    + simpleHash(`${CONFIG.userUid}${JSON.stringify(params || {})}`)
}

export function logServiceError(error, endpoint, statusCode, body, headers) {
  let message = omitBy({ error, endpoint, body }, isNil)
  if (error instanceof Error) {
    message = JSON.parse(JSON.stringify(message, Object.getOwnPropertyNames(error)))
  }
  if (!window._LTracker) {
    return
  }
  window._LTracker.push({
    correlationId: get(headers, 'x-amzn-requestid'),
    interface: window._LTracker.interface,
    tag: window._LTracker.tag,
    endpoint,
    statusCode,
    response: message,
  })
}

/**
 * Transform an JSON object to a query string
 * @param params
 * @returns {string}
 */
export function toQueryString(params) {
  return '?' + Object.keys(params).map(k => {
    const name = encodeURIComponent(k)
    if (Array.isArray(params[k])) {
      return params[k].map(val => `${name}[]=${encodeURIComponent(val)}`).join('&')
    }
    return `${name}=${encodeURIComponent(params[k])}`
  }).join('&')
}
